import { createConsumer, createSubscriber, IEvent } from '@cian/events-log';
import { Microfrontend } from '@cian/mf-react';
import { ModalWindow } from '@cian/ui-kit';
import * as React from 'react';

import { ComplainModalError } from './ComplainModalError';
import { ComplainModalLoader } from './ComplainModalLoader';
import { getModerationComplainMicrofrontend } from '../../services/getModerationComplainMicrofrontend';
import { useApplicationContext } from '../ApplicationContext';

import type { IMicrofrontendManifest } from '@cian/mf-registry/shared';

interface IComplainModalContainerProps {
  offerId: number | null;
  onClose(): void;
}

export const ComplainModalContainer = ({ offerId, onClose }: IComplainModalContainerProps) => {
  const context = useApplicationContext();
  const [manifest, setManifest] = React.useState<IMicrofrontendManifest | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isError, setIsError] = React.useState(false);

  const open = offerId !== null;

  const loadManifest = React.useCallback(async () => {
    setIsLoading(true);
    setIsError(false);

    const result = await getModerationComplainMicrofrontend(context);

    setIsLoading(false);
    if (result) {
      setManifest(result.manifest);
    } else {
      setIsError(true);
    }
  }, [context]);

  // грузим manifest при первом открытии
  React.useEffect(() => {
    if (open && !manifest && !isLoading && !isError) {
      loadManifest();
    }
  }, [open, manifest, isLoading, isError, loadManifest]);

  // подписка на закрытие микрофронта изнутри
  React.useEffect(() => {
    const consumer = createConsumer({ topic: 'complaintsModal' });
    const subscriber = createSubscriber((event: IEvent<unknown>) => {
      if (event.type === 'popup_close') {
        onClose();
      }
    });
    consumer.subscribe(subscriber);

    return () => {
      consumer.unsubscribe(subscriber);
      consumer.destroy();
    };
  }, [onClose]);

  const renderContent = () => {
    if (manifest && offerId !== null) {
      return (
        <Microfrontend
          name={manifest.microfrontendName}
          image={manifest.imageVersion}
          runtime={manifest.runtimeName}
          instance={manifest.instanceId}
          parameters={{ offerId }}
        />
      );
    }

    if (isError) {
      return <ComplainModalError isLoading={isLoading} onRetry={loadManifest} />;
    }

    return <ComplainModalLoader />;
  };

  return <ModalWindow open={open} onClose={onClose} width="100%" maxWidth={600} content={open && renderContent()} />;
};
